import type { Lang } from "./store";
import { getAliases } from "./seoAliases";

export const SITE_URL = "https://calculatorbangla.com";
export const SITE_NAME = "Bangla Calculator";

export interface SeoOptions {
  title: string;
  description: string;
  path: string; // route path starting with /
  image?: string;
  type?: "website" | "article";
  faq?: { q: string; a: string }[];
  breadcrumbs?: { name: string; path: string }[];
}

type Meta = { title?: string } | { name: string; content: string } | { property: string; content: string };
type LinkTag = { rel: string; href: string; hrefLang?: string };
type ScriptTag = { type: string; children: string };

function abs(path: string): string {
  return `${SITE_URL}${path === "/" ? "" : path}`;
}

export function buildSeo(opts: SeoOptions): {
  meta: Meta[];
  links: LinkTag[];
  scripts: ScriptTag[];
} {
  const url = abs(opts.path);
  const image = opts.image ?? `${SITE_URL}/og-default.png`;

  // Auto-derive keyword variants for this path (e.g. "bmi", "body mass index").
  const aliases = getAliases(opts.path);
  const keywordsContent = aliases.length > 0 ? aliases.join(", ") : undefined;

  const meta: Meta[] = [
    { title: opts.title },
    { name: "description", content: opts.description },
    ...(keywordsContent ? [{ name: "keywords", content: keywordsContent }] : []),
    { name: "robots", content: "index,follow,max-image-preview:large" },
    // Open Graph
    { property: "og:type", content: opts.type ?? "website" },
    { property: "og:site_name", content: SITE_NAME },
    { property: "og:title", content: opts.title },
    { property: "og:description", content: opts.description },
    { property: "og:url", content: url },
    { property: "og:image", content: image },
    { property: "og:locale", content: "en_BD" },
    { property: "og:locale:alternate", content: "bn_BD" },
    // Twitter
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: opts.title },
    { name: "twitter:description", content: opts.description },
    { name: "twitter:image", content: image },
  ];

  const links: LinkTag[] = [
    { rel: "canonical", href: url },
    { rel: "alternate", hrefLang: "en-BD", href: url },
    { rel: "alternate", hrefLang: "bn-BD", href: url },
    { rel: "alternate", hrefLang: "x-default", href: url },
  ];

  const scripts: ScriptTag[] = [];

  // WebApplication + SoftwareApplication (dual-type) schema
  scripts.push({
    type: "application/ld+json",
    children: JSON.stringify({
      "@context": "https://schema.org",
      "@type": ["WebApplication", "SoftwareApplication"],
      name: opts.title,
      url,
      description: opts.description,
      applicationCategory: "UtilitiesApplication",
      applicationSubCategory: "Calculator",
      operatingSystem: "Any",
      browserRequirements: "Requires JavaScript. Modern browser.",
      inLanguage: ["en-BD", "bn-BD"],
      isAccessibleForFree: true,
      offers: { "@type": "Offer", price: "0", priceCurrency: "BDT" },
      publisher: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
    }),
  });

  // Breadcrumbs — auto-generate Home → page when caller didn't provide any
  const breadcrumbs =
    opts.breadcrumbs && opts.breadcrumbs.length > 0
      ? opts.breadcrumbs
      : opts.path === "/"
        ? [{ name: "Home", path: "/" }]
        : [
            { name: "Home", path: "/" },
            { name: opts.title, path: opts.path },
          ];
  scripts.push({
    type: "application/ld+json",
    children: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: breadcrumbs.map((b, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: b.name,
        item: abs(b.path),
      })),
    }),
  });

  // FAQ
  if (opts.faq && opts.faq.length > 0) {
    scripts.push({
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: opts.faq.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }),
    });
  }

  return { meta, links, scripts };
}

/** Pick an SEO-friendly localized string. Falls back to English. */
export function pickLang<T extends { en: string; bn: string }>(v: T, lang: Lang): string {
  return v[lang] || v.en;
}
